import { memo, ReactNode, Component, ErrorInfo } from 'react';
import { BackgroundLayers } from './BackgroundLayers';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useSurpriseMode } from '@/hooks/useSurpriseMode';
import { cn } from '@/lib/utils';

interface AppShellV2Props {
  children: ReactNode;
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary for V2 shell - falls back to children without effects
 */
class V2ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('UI V2 Shell Error - falling back to classic:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render children without V2 effects
      return this.props.children;
    }

    return this.props.children;
  }
}

/**
 * AppShellV2 - Enhanced app wrapper with V2 background layers
 * Features:
 * - Background gradient + aurora + grid
 * - Surprise mode hooks
 * - Error boundary for safe fallback
 */
const AppShellV2Inner = memo(function AppShellV2Inner({ 
  children, 
  className 
}: AppShellV2Props) {
  const { uiV2Enabled } = useUIV2Safe();
  
  // Initialize surprise mode listeners
  useSurpriseMode();
  
  return (
    <div className={cn('relative min-h-screen', className)}>
      {/* Background layers */}
      {uiV2Enabled && <BackgroundLayers />}
      
      {/* Main content */}
      <div className="relative z-0">
        {children}
      </div>
    </div>
  );
});

/**
 * Exported wrapper with error boundary
 */
export const AppShellV2 = memo(function AppShellV2(props: AppShellV2Props) {
  return (
    <V2ErrorBoundary>
      <AppShellV2Inner {...props} />
    </V2ErrorBoundary>
  );
});
